import { createContext, useContext, useEffect, useState } from 'react';
// Global app context: exposes current Firebase user and a loading flag
import type { AppContextType, AppProviderProps, UserRole } from '../types/app';
import { onAuthChange } from '../lib/firebase';
import type { User } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../lib/firebase';
import { fcmService } from '../utils/fcm';

const AppContext = createContext<AppContextType>({ user: null, userRole: null, loading: true });

// Consumer hook — throws when used outside provider to catch misuse early
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to Firebase auth state changes and reflect them in context
    const unsubscribe = onAuthChange(async (user) => {
      setUser(user);
      
      if (user) {
        // Keep loading true while fetching user role
        setLoading(true);
        // Fetch user role from Firestore
        try {
          const db = getFirestore(app);
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserRole(userData.Role as UserRole || 'user');
          } else {
            // Default to 'user' role if no document exists yet
            setUserRole('user');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setUserRole('user'); // Default to 'user' on error
        }
        // After login, silently refresh and persist this device's FCM token (if possible)
        try {
          await fcmService.refreshTokenOnLogin(user.uid);
        } catch (err) {
          console.warn('FCM login refresh failed:', err);
        }

        // Role is now determined, stop loading
        setLoading(false);
      } else {
        setUserRole(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <AppContext.Provider value={{ user, userRole, loading }}>
      {children}
    </AppContext.Provider>
  );
};
